<template>
  <el-row class="wrapper flex-fill px-3 px-md-5 mt-5">
    <el-col :sm="{ span: 12, offset: 6 }" :md="{ span: 12, offset: 6 }" :lg="{ span: 8, offset: 8 }" :xl="{ span: 10, offset: 7 }">
      <div class="d-flex flex-column gap-3 justify-content-center h-100 form-wrapper">
        <div class="d-flex mb-4 align-items-center justify-content-center logo">
          <img id="logo" :src="userStore.domain?.logo ?? '/assets/cekoon_logo.png'" />
        </div>
        <div class="bg-white p-4">
          <h3 class="mb-4">{{ $t('components.resetPassword.title') }}</h3>
          <el-form ref="dataForm" :model="forgotPasswordState" label-position="top" require-asterisk-position="right" @submit="submitForgotPassword">
            <el-form-item
              :label="$t('components.resetPassword.userName')"
              prop="username"
              required
              class="form-text"
              :rules="{
                required: true,
                message: $t('error.vuelidate.required'),
              }"
            >
              <el-input 
                v-model="forgotPasswordState.username" 
                class="custom-input" 
                type="text" 
                placeholder="john-doe"
              />
            </el-form-item>
            <el-form-item
              :label="$t('components.resetPassword.newPassword')"
              prop="password"
              required
              class="form-text"
              :rules="{
                required: true,
                message: $t('error.vuelidate.required'),
              }"
            >
              <el-input 
                v-model="forgotPasswordState.password" 
                class="custom-input" 
                type="password" 
                autocomplete="off"
                placeholder="******"
              />
            </el-form-item>
            <el-form-item
              :label="$t('components.resetPassword.repeatNewPassword')"
              prop="password_repeat"
              required
              class="form-text"
              :rules="{
                required: true,
                message: $t('error.vuelidate.required'),
              }"
            >
              <el-input 
                v-model="forgotPasswordState.password_repeat" 
                class="custom-input" 
                type="password" 
                autocomplete="off" 
                placeholder="******"
              />
            </el-form-item>
            <el-form-item>
              <div class="d-grid w-100">
                <el-button class="button" type="primary" native-type="submit" :disabled="isLoading">
                  <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <span v-else>{{ $t('components.resetPassword.submit') }}</span>
                </el-button>
              </div>
            </el-form-item>
          </el-form>
        </div>
        <div class="d-flex flex-column align-items-center gap-3 fw-normal mt-4">
          <el-text><a href="/login" class="text-primary text-decoration-none">{{ $t('components.login.backToLoginButton') }}</a></el-text>
        </div>
      </div>
    </el-col>

    <el-dialog v-model="dialogVisible" class="dialog-container" title="">
      <div>
        <div style="text-align: center">
          <p class="dialog">{{ $t('components.login.emailSent') }}</p>
        </div>
      </div>

      <template #footer>
        <span class="dialog-footer">
          <el-button class="button" @click="handleOK">{{ $t('components.login.ok') }}</el-button>
        </span>
      </template>
    </el-dialog>
  </el-row>
</template>

<script lang="ts">
import { Vue, Component } from 'vue-facing-decorator';
import { ElForm, ElMessage } from 'element-plus';
import { endpointAuthSystem } from '@/services/api/api';
import { useUserStore } from '@/stores/userStore';
import { useRoute, useRouter } from 'vue-router';

interface ForgotPasswordForm {
  username: string;
  password: string;
  password_repeat: string;
}

@Component
export default class ForgotPassword extends Vue {
  userStore = useUserStore();
  forgotPasswordState: ForgotPasswordForm = {
    username: '',
    password: '',
    password_repeat: '',
  };
  dialogVisible = false;
  isLoading = false;
  router = useRouter();
  route = useRoute();

  async submitForgotPassword(event: Event | null = null): Promise<void> {
    if (event) event.preventDefault();

    const dataForm = this.$refs.dataForm as InstanceType<typeof ElForm>;
    dataForm?.validate(async (valid: boolean) => {
      if (valid) {
        this.isLoading = true;
        try {
          const formData = new FormData();
          formData.append('uid_field', this.forgotPasswordState.username);
          formData.append('password', this.forgotPasswordState.password);
          formData.append('password_repeat', this.forgotPasswordState.password_repeat);

          const api = endpointAuthSystem();
          const response = await api.post('api/cekoon/login/authentik/reset-password', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          if (response.data.Status === 'Success') {
            this.dialogVisible = true;
          }
        } catch (error: unknown) {
          console.error('Submission failed:', error);
          ElMessage.error(this.$t('error.register.submission'));
        } finally {
          this.isLoading = false;
        }
      } else {
        ElMessage.error(this.$t('error.register.validation'));
      }
    });
  }

  handleOK(): void {
    this.dialogVisible = false;
    this.router.push('/login');
  }
}
</script>
<style scoped>
.button {
  margin-top: 20px;
  width: 100%;
  color: white;
  background-color: #1d1e19;
  border: none;
}

.button:hover {
  background-color: #353535;
}

#logo {
  max-height: 3.4rem;
}

.custom-input::v-deep(.el-input__wrapper) {
  padding: 1px !important;
}
.custom-input::v-deep(.el-input__inner) {
  border-radius: 4px !important;
  padding: 1px 11px !important;
}
</style>
